/**
 * frontend/src/components/RecorridoGuiado.ts
 * ===========================================
 * Recorrido guiado: acompaña la primera conciliación de punta a punta.
 *
 * No explica pantallas: señala dónde actuar y espera a que el usuario lo
 * haga. Pulsa Preferencias y el recorrido sigue adentro, campo por campo, de
 * arriba hacia abajo; guarda y el recorrido pasa a la carga del extracto; y así
 * hasta el reporte. Cada paso avanza solo cuando la acción ocurre. Con → se
 * avanza en los pasos de lectura (o se hace el clic que el paso pide) y con ←
 * se vuelve; «Omitir» lo cierra. El botón ? de la cabecera lo empieza de nuevo.
 *
 * Si el usuario abre algo que el recorrido no señala —un diálogo, un perfil
 * guardado—, el recorrido se retira y vuelve cuando su paso está de nuevo en
 * pantalla. Nunca bloquea lo que el usuario está haciendo.
 *
 * Lo que cada paso señala se marca con `data-recorrido` o con un `id` estable.
 * `tests/recorrido.test.cjs` verifica que cada ancla exista en el marcado.
 */

import { icon } from "../utils/icons";

/** Cómo termina cada paso. */
type Accion =
  /** Se pulsa el elemento señalado. → hace el clic. */
  | "clic"
  /** Se completa un campo. → avanza; si el paso tiene `valido`, solo cuando lo es. */
  | "escribir"
  /** Se lee y se sigue con →. */
  | "leer"
  /** Lo hace el usuario (cargar un archivo, elegir un perfil): avanza solo, con `hasta`. */
  | "hacer";

interface Paso {
  seccion: string;
  /** Selector del elemento que se señala. */
  ancla: string;
  titulo: string;
  texto: string;
  accion: Accion;
  /** Para «escribir»: el campo tiene un valor utilizable. */
  valido?: () => boolean;
  /** Para «hacer»: la acción ya ocurrió. */
  hasta?: () => boolean;
  /** Si se cumple, el paso no aplica y se pasa al siguiente. */
  saltarSi?: () => boolean;
  /** Lo que además queda sin velo porque hay que verlo para cumplir el paso. */
  tambien?: string[];
  /**
   * Para «hacer»: el paso se puede saltear con →. Quien solo quiere conocer la
   * aplicación sigue por la vista general, desde este paso de `VISTA`.
   */
  alSaltar?: number;
}

// ── Estado de la aplicación, leído del DOM ──────────────────────────────────

function visible(selector: string): HTMLElement | null {
  const el = document.querySelector<HTMLElement>(selector);
  return el && el.getClientRects().length > 0 ? el : null;
}

const wizardAbierto = () => Boolean(document.querySelector(".wizard-overlay"));
/** El asistente abierto es el de este archivo: el de la otra carga no cuenta. */
const asistenteDe = (grupo: "extracto" | "libro") =>
  Boolean(document.querySelector(`.wizard-modal[data-tipo-fuente="${grupo === "libro" ? "libro_mayor" : "extracto"}"]`));
const perfilElegido = (grupo: string) => Boolean(visible(`[data-recorrido="${grupo}"] #ps-editar`));
const valor = (selector: string) => (document.querySelector<HTMLInputElement>(selector)?.value ?? "").trim();

/**
 * La vista del archivo en el paso de columnas: el título y la tabla, del
 * tamaño de sus filas. El panel que la contiene es más alto, y descubrirlo
 * entero dejaba a la vista un espacio en blanco debajo de un archivo corto.
 */
const VISTA_DEL_ARCHIVO = ".wiz-paso2__preview .wiz-encabezado";

/**
 * El asistente de perfiles, campo por campo y en el orden en que se dibuja.
 * Lo mismo para el extracto y para el libro: si se eligió un perfil guardado
 * en lugar de crear uno, estos pasos no aplican.
 */
function pasosDelAsistente(grupo: "extracto" | "libro", seccion: string): Paso[] {
  // No aplica si eligió un perfil guardado, o si el asistente abierto es el
  // del otro archivo.
  const saltarSi = () => perfilElegido(grupo) || (wizardAbierto() && !asistenteDe(grupo));
  return [
    // Primero la fila del encabezado: todo lo detectado depende de ella.
    { seccion, ancla: ".wiz-detect__workspace", accion: "leer", saltarSi,
      titulo: "Fila del encabezado", texto: "Haga clic en la fila que tiene los nombres de las columnas." },
    { seccion, ancla: ".wiz-detect__overview", accion: "leer", saltarSi,
      titulo: "Lo detectado", texto: "Período, formatos y columnas reconocidas. Revise que coincidan con su archivo." },
    { seccion, ancla: "#wiz-next", accion: "clic", saltarSi,
      titulo: "Configure el mapeo", texto: "Pasa a asignar las columnas." },
    // Mientras elige columnas, su archivo queda a la vista: ahí se resalta cada una.
    { seccion, ancla: '[data-recorrido="columnas"]', tambien: [VISTA_DEL_ARCHIVO], accion: "leer", saltarSi,
      titulo: "Columnas",
      // En el libro, la cuenta y su código están dentro de esta sección: se
      // nombran acá y no en un paso aparte que señalaría lo mismo.
      texto: grupo === "libro"
        ? "Elija la columna de cada dato y la cuenta con su código, o marque si el archivo trae una sola."
        : "Elija la columna de su archivo para cada dato. Se resalta en la vista de su archivo." },
    { seccion, ancla: '[data-recorrido="importe"]', tambien: [VISTA_DEL_ARCHIVO], accion: "leer", saltarSi,
      titulo: "Importe", texto: "Cómo vienen los importes: débito y crédito separados, con signo, o monto y tipo." },
    { seccion, ancla: '[data-recorrido="comparacion"]', accion: "escribir", saltarSi,
      titulo: "Tolerancia", texto: "Días de diferencia aceptados entre la fecha del banco y la del asiento." },
    { seccion, ancla: '[data-recorrido="formatos"]', tambien: [VISTA_DEL_ARCHIVO], accion: "leer",
      saltarSi: () => saltarSi() || (wizardAbierto() && !visible('[data-recorrido="formatos"]')),
      titulo: "Formatos", texto: "Cómo vienen las fechas y los números. Revise que coincidan." },
    { seccion, ancla: "#wiz-validar", accion: "clic", saltarSi,
      titulo: "Verifique", texto: "Lee el archivo con este mapeo." },
    // El resultado de la verificación queda a la vista: es lo que se evalúa.
    { seccion, ancla: "#wiz-next-tip", tambien: ["#wf-validar-result"], accion: "clic", saltarSi,
      titulo: "Continúe", texto: "Si el resultado es correcto. Si no, corrija las columnas y verifique de nuevo." },
    { seccion, ancla: '[data-recorrido="nombre-perfil"]', accion: "escribir", saltarSi,
      titulo: "Nombre del perfil", texto: "Para reconocerlo la próxima vez.", valido: () => Boolean(valor("#ws-nombre")) },
    { seccion, ancla: '[data-recorrido="banco"]', accion: "escribir", saltarSi,
      titulo: "Banco o sistema", texto: "De dónde viene el archivo. Es opcional." },
    { seccion, ancla: "#wiz-guardar", accion: "clic", saltarSi,
      titulo: "Guarde el perfil", texto: "Queda listo para los próximos archivos." },
  ];
}

export const PASOS: readonly Paso[] = [
  // Preferencias, de arriba hacia abajo.
  { seccion: "Preferencias", ancla: "#btn-settings", accion: "clic",
    titulo: "Empiece por sus preferencias", texto: "Se completan una vez y valen para todas sus conciliaciones." },
  { seccion: "Preferencias", ancla: '[data-recorrido="empresa"]', accion: "escribir",
    titulo: "Nombre de la empresa", texto: "Aparece en el encabezado del reporte. Es opcional." },
  { seccion: "Preferencias", ancla: '[data-recorrido="nombre-archivo"]', accion: "escribir",
    titulo: "Nombre del archivo", texto: "Así se llamarán el Excel y el PDF. Puede dejar el sugerido." },
  { seccion: "Preferencias", ancla: '[data-recorrido="destinatarios"]', accion: "escribir",
    // Sin `valido`: los destinatarios no son obligatorios. La aplicación los
    // pide recién al enviar el reporte por correo.
    titulo: "Destinatarios", texto: "El correo que recibe el reporte." },
  { seccion: "Preferencias", ancla: '[data-recorrido="umbral"]', accion: "escribir",
    titulo: "Umbral", texto: "Las diferencias menores a este monto no se informan. Con 0 se informan todas." },
  { seccion: "Preferencias", ancla: "#btn-prefs-save", accion: "clic",
    titulo: "Guarde las preferencias", texto: "Después siguen los archivos." },

  // Extracto y su perfil.
  { seccion: "Extracto", ancla: '[data-recorrido="extracto"]', accion: "hacer", alSaltar: 0,
    titulo: "Cargue el extracto", texto: "El archivo del banco, en XLSX o CSV. Puede arrastrarlo.",
    hasta: () => Boolean(visible('[data-recorrido="extracto"] [data-recorrido="perfil"]')) },
  { seccion: "Extracto", ancla: '[data-recorrido="extracto"] [data-recorrido="perfil"]', accion: "hacer", alSaltar: 0,
    // La lista de perfiles guardados se abre fuera del recuadro: también queda a la vista.
    tambien: [".profile-picker__panel"],
    titulo: "Perfil de lectura", texto: "Dice cómo leer las columnas de este archivo. Créelo con +, o elíjalo si ya lo tiene.",
    hasta: () => asistenteDe("extracto") || perfilElegido("extracto") },

  ...pasosDelAsistente("extracto", "Perfil del extracto"),

  // Libro mayor: el mismo camino.
  { seccion: "Libro mayor", ancla: '[data-recorrido="libro"]', accion: "hacer", alSaltar: 1,
    titulo: "Cargue el libro mayor", texto: "Del mismo período que el extracto.",
    hasta: () => Boolean(visible('[data-recorrido="libro"] [data-recorrido="perfil"]')) },
  { seccion: "Libro mayor", ancla: '[data-recorrido="libro"] [data-recorrido="perfil"]', accion: "hacer", alSaltar: 1, tambien: [".profile-picker__panel"],
    titulo: "Perfil de lectura", texto: "Créelo con +, como el anterior, o elíjalo si ya lo tiene.",
    hasta: () => asistenteDe("libro") || perfilElegido("libro") },
  ...pasosDelAsistente("libro", "Perfil del libro"),

  // Conciliar, revisar y recibir.
  { seccion: "Conciliación", ancla: "#iniciar-tip", accion: "clic",
    titulo: "Inicie la conciliación", texto: "Se comparan los movimientos. Tarda unos segundos." },
  { seccion: "Revisión", ancla: "#resumen-content", accion: "leer",
    titulo: "Revise el resultado", texto: "Saldos y diferencias por período. Todavía no se envió nada." },
  // El visor, en el orden en que se lee: las pestañas con la tabla, el
  // buscador y las descargas. Aparece un instante después que el resumen; el
  // recorrido espera a que esté en pantalla.
  { seccion: "Revisión", ancla: ".visor__pestanas", accion: "leer", tambien: [".visor__panel"],
    titulo: "Los movimientos", texto: "Solo en banco, solo en contabilidad, conciliados y agrupados, cada uno con su total." },
  { seccion: "Revisión", ancla: ".visor__buscar", accion: "leer",
    titulo: "Busque", texto: "Por descripción, asiento o importe, con o sin tildes." },
  { seccion: "Revisión", ancla: ".visor__descargas", accion: "leer",
    titulo: "Descárguelo", texto: "El Excel y el PDF, tal como llegarían por correo, sin enviar nada." },
  // Se señala la decisión y se sigue: con «clic», la flecha apretaba el botón y
  // enviaba el reporte —sin destinatarios, ni siquiera se llegaba a ver el
  // resto del recorrido—. Enviar lo decide el usuario, no el recorrido.
  { seccion: "Revisión", ancla: ".review-view__decision", accion: "leer",
    titulo: "Envíe el reporte", texto: "Por correo, cuando lo necesite. Si faltan destinatarios, indíquelos aquí." },
  { seccion: "Reporte", ancla: ".result-view:has(.result-view__icon--ok) .result-view__header", accion: "leer",
    // Solo si lo envió: si no, el recorrido sigue en la revisión.
    saltarSi: () => !visible(".result-view__icon--ok"),
    titulo: "Reporte enviado", texto: "Revise su correo: llegan el Excel y el PDF." },
  { seccion: "Reporte", ancla: ".reporte-flotante", accion: "leer",
    titulo: "¿Algo no cuadra?", texto: "Repórtelo desde aquí, con capturas si hace falta." },
  // Solo el botón: el formulario se abre cuando el usuario quiera opinar.
  { seccion: "Reporte", ancla: ".opinion-flotante", accion: "leer",
    titulo: "Su opinión", texto: "Cuéntenos cómo le resulta ConciliA." },
];

/**
 * La vista general: para quien abre la aplicación solo para conocerla. Se
 * entra salteando con → la carga de un archivo o de un perfil, y muestra lo
 * que queda sin necesidad de cargar nada. Si después carga sus archivos, el
 * recorrido retoma el camino completo desde donde lo salteó.
 */
export const VISTA: readonly Paso[] = [
  { seccion: "Libro mayor", ancla: '[data-recorrido="libro"]', accion: "leer",
    titulo: "Libro mayor", texto: "Del mismo período que el extracto. Cada archivo se lee con su perfil." },
  { seccion: "Conciliación", ancla: "#iniciar-tip", accion: "leer",
    titulo: "Conciliar", texto: "Compara cada movimiento. Usted revisa el resultado antes de que se envíe el reporte." },
  { seccion: "Conciliación", ancla: "#btn-recorrido", accion: "leer",
    titulo: "Cuando cargue sus archivos", texto: "El recorrido sigue desde aquí. También puede volver a empezarlo." },
];

/** Una conciliación rechazada o con error no llega al reporte: ahí el recorrido termina. */
const terminoSinReporte = () => Boolean(visible(".result-view__icon--cancel, .result-view__icon--error"));

const SECCIONES = [...new Set(PASOS.map(p => p.seccion))];

// ── Si ya lo hizo ───────────────────────────────────────────────────────────

const CLAVE = "conciliador_recorrido";

/** Vacío en instalaciones sin usuarios: la clave queda sin sufijo. */
let usuarioActivo = "";

/** Define de quién es el recorrido que se lee y se guarda. */
export function usarRecorridoDe(usuario: string): void {
  usuarioActivo = usuario;
}

function clave(): string {
  return usuarioActivo ? `${CLAVE}:${usuarioActivo}` : CLAVE;
}

/**
 * `null` si nunca lo vio; `"cerrado"` si lo terminó o lo omitió; o el paso del
 * camino completo que salteó para ver la vista general, desde donde se retoma.
 */
function leerAvance(): "cerrado" | number | null {
  try {
    const guardado = localStorage.getItem(clave());
    if (guardado === "cerrado") return "cerrado";
    const salteado = guardado?.match(/^explorado:(\d+)$/);
    return salteado ? Number(salteado[1]) : null;
  } catch {
    return null;
  }
}

function guardarAvance(valor: string): void {
  try {
    localStorage.setItem(clave(), valor);
  } catch {
    // Sin almacenamiento el recorrido vuelve a aparecer: molesta, pero no rompe nada.
  }
}

function marcarCerrado(): void {
  guardarAvance("cerrado");
}

// ── API ─────────────────────────────────────────────────────────────────────

let detenerActivo: (() => void) | null = null;

/** Empieza el recorrido si este usuario todavía no lo terminó ni lo omitió. */
export function iniciarRecorrido(): void {
  const avance = leerAvance();
  if (avance === "cerrado" || detenerActivo) return;
  // Si vio la vista general, el camino completo espera, en silencio, a que
  // cargue el archivo que había salteado.
  arrancar(avance ?? 0, avance !== null);
}

/** Al salir: el recorrido es de quien estaba, no de quien entre después. */
export function detenerRecorrido(): void {
  detenerActivo?.();
}

/** El botón de ayuda: lo empieza de nuevo, desde el principio. */
export function reiniciarRecorrido(): void {
  detenerActivo?.();
  try {
    localStorage.removeItem(clave());
  } catch {
    // Sin almacenamiento, igual arranca.
  }
  arrancar(0, false);
}

// ── Motor ───────────────────────────────────────────────────────────────────

/** Espacio entre el elemento y el borde del velo. */
const MARGEN_FOCO = 4;
/** Distancia mínima de la tarjeta al borde de la pantalla. */
const MARGEN_PANTALLA = 16;
/** Por debajo de este ancho la tarjeta va abajo, de lado a lado. */
const ANCHO_ANGOSTO = 600;
/** Cuánto se funde el borde del velo hacia lo que queda a la vista, en px. */
const DIFUMINADO = 3;
/** Cuánto espera antes de retirarse si el paso deja de estar a la vista. */
const GRACIA_PARA_OCULTAR_MS = 250;
/** Lo que dura el deslizamiento del velo de un paso al siguiente; igual a `--duration-view`. */
const DURACION_CAMBIO_MS = 340;
/** Espera para enfocar un campo después del foco inicial de su diálogo. */
const PAUSA_FOCO_MS = 60;
/** Cada cuánto se revisa el estado, además de los cambios del DOM. */
const INTERVALO_MS = 300;

/** El diálogo que está arriba de todo, si hay uno. */
function dialogoSuperior(): Element | null {
  const abiertos = document.querySelectorAll(".modal-overlay, .wizard-overlay");
  return abiertos[abiertos.length - 1] ?? null;
}

/** El paso se puede mostrar: su elemento está en pantalla y ningún diálogo lo tapa. */
function disponible(paso: Paso): HTMLElement | null {
  const el = visible(paso.ancla);
  if (!el) return null;
  const arriba = dialogoSuperior();
  return !arriba || arriba.contains(el) ? el : null;
}

/** El botón que pulsa un paso «clic»: el elemento mismo, o el que contiene. */
function botonDe(el: HTMLElement | null): HTMLButtonElement | null {
  if (!el) return null;
  return el instanceof HTMLButtonElement ? el : el.querySelector("button");
}

/**
 * Dónde va la tarjeta: el lugar que menos tapa de lo que el paso deja a la
 * vista. Se prueban, en este orden, al costado del elemento (el recorrido baja
 * por los campos, y debajo taparía el siguiente), debajo, arriba y las
 * esquinas de la pantalla; ante un empate, gana el primero.
 */
export function ubicarTarjeta(
  rects: readonly DOMRectReadOnly[], ancho: number, alto: number,
  pantalla = { ancho: innerWidth, alto: innerHeight },
): { left: number; top: number } {
  const [r] = rects;
  const m = MARGEN_PANTALLA;
  const sep = MARGEN_FOCO + MARGEN_PANTALLA;
  const entre = (v: number, tamano: number, limite: number) => Math.min(Math.max(v, m), limite - tamano - m);
  const x = (v: number) => entre(v, ancho, pantalla.ancho);
  const y = (v: number) => entre(v, alto, pantalla.alto);

  const candidatos = [
    { left: x(r.right + sep), top: y(r.top) },
    { left: x(r.left - sep - ancho), top: y(r.top) },
    { left: x(r.left), top: y(r.bottom + sep) },
    { left: x(r.left), top: y(r.top - sep - alto) },
    { left: x(pantalla.ancho - ancho), top: y(pantalla.alto - alto) },
    { left: x(0), top: y(pantalla.alto - alto) },
    { left: x(pantalla.ancho - ancho), top: y(0) },
    { left: x(0), top: y(0) },
  ];
  const tapa = (c: { left: number; top: number }) => rects.reduce((total, d) => {
    const w = Math.min(c.left + ancho, d.right + MARGEN_FOCO) - Math.max(c.left, d.left - MARGEN_FOCO);
    const h = Math.min(c.top + alto, d.bottom + MARGEN_FOCO) - Math.max(c.top, d.top - MARGEN_FOCO);
    return total + Math.max(0, w) * Math.max(0, h);
  }, 0);
  return candidatos.reduce((mejor, c) => (tapa(c) < tapa(mejor) ? c : mejor));
}

/**
 * La parte de `el` que se ve: su rectángulo recortado por cada contenedor
 * con scroll o con el desborde oculto, y por la pantalla. `null` si no se ve.
 */
function parteVisible(el: Element): DOMRect | null {
  let { left, top, right, bottom } = el.getBoundingClientRect();
  for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
    const { overflowX, overflowY } = getComputedStyle(p);
    if (overflowX === "visible" && overflowY === "visible") continue;
    const c = p.getBoundingClientRect();
    left = Math.max(left, c.left); top = Math.max(top, c.top);
    right = Math.min(right, c.right); bottom = Math.min(bottom, c.bottom);
  }
  left = Math.max(left, 0); top = Math.max(top, 0);
  right = Math.min(right, innerWidth); bottom = Math.min(bottom, innerHeight);
  return right > left && bottom > top ? new DOMRect(left, top, right - left, bottom - top) : null;
}

function esEditable(el: Element | null): boolean {
  return el instanceof HTMLInputElement || el instanceof HTMLSelectElement
    || el instanceof HTMLTextAreaElement || (el instanceof HTMLElement && el.isContentEditable);
}

/**
 * @param desde      el paso del camino completo por el que empieza.
 * @param silencioso no se muestra hasta que el usuario cumpla el primer paso:
 *                   es el retorno de la vista general, y no tiene que volver a
 *                   pedirle el archivo cada vez que abre la aplicación.
 */
function arrancar(desde: number, silencioso: boolean): void {
  const raiz = document.createElement("div");
  raiz.className = "recorrido";
  raiz.classList.add("recorrido--oculto");
  raiz.innerHTML = `
    <svg class="recorrido__velo" aria-hidden="true" focusable="false">
      <defs>
        <filter id="recorrido-difuso" x="-10%" y="-10%" width="120%" height="120%">
          <feGaussianBlur stdDeviation="${DIFUMINADO}" />
        </filter>
        <mask id="recorrido-mascara">
          <rect width="100%" height="100%" fill="white" />
          <g class="recorrido__huecos" filter="url(#recorrido-difuso)"></g>
        </mask>
      </defs>
      <rect class="recorrido__sombra" width="100%" height="100%" mask="url(#recorrido-mascara)" />
    </svg>
    <div class="recorrido__tarjeta" role="dialog" aria-labelledby="recorrido-titulo" aria-describedby="recorrido-texto">
      <p class="recorrido__etapa" id="recorrido-etapa"></p>
      <h2 class="recorrido__titulo" id="recorrido-titulo"></h2>
      <p class="recorrido__texto" id="recorrido-texto"></p>
      <div class="recorrido__pie">
        <button class="btn btn--link recorrido__omitir" type="button">Omitir</button>
        <span class="recorrido__progreso"></span>
        <button class="btn btn--secondary recorrido__flecha" type="button" data-accion="atras" aria-label="Paso anterior">${icon("back")}</button>
        <button class="btn btn--primary recorrido__flecha" type="button" data-accion="siguiente" aria-label="Paso siguiente">${icon("arrow")}</button>
      </div>
    </div>
  `;
  document.body.appendChild(raiz);

  const huecos = raiz.querySelector<SVGGElement>(".recorrido__huecos")!;
  const tarjeta = raiz.querySelector<HTMLElement>(".recorrido__tarjeta")!;
  const etiqueta = raiz.querySelector<HTMLElement>("#recorrido-etapa")!;
  const titulo = raiz.querySelector<HTMLElement>("#recorrido-titulo")!;
  const texto = raiz.querySelector<HTMLElement>("#recorrido-texto")!;
  const progreso = raiz.querySelector<HTMLElement>(".recorrido__progreso")!;
  const atras = raiz.querySelector<HTMLButtonElement>('[data-accion="atras"]')!;
  const siguiente = raiz.querySelector<HTMLButtonElement>('[data-accion="siguiente"]')!;
  const omitir = raiz.querySelector<HTMLButtonElement>(".recorrido__omitir")!;

  /** El camino que se recorre: el completo o la vista general. */
  let lista: readonly Paso[] = PASOS;
  let actual = desde;
  /** El paso del camino completo desde el que se entró a la vista general. */
  let salteadoEn = -1;
  let mostrado = -1;
  let objetivo: HTMLElement | null = null;
  let finDelCambio: ReturnType<typeof setTimeout> | undefined;
  let oculto = true;
  let esperaParaOcultar: ReturnType<typeof setTimeout> | undefined;
  const controlador = new AbortController();
  const senal = { signal: controlador.signal };

  function avanzar(): void {
    actual++;
    evaluar();
  }

  /** → sobre un paso que se puede saltear: a la vista general. */
  function saltear(paso: Paso): void {
    salteadoEn = actual;
    lista = VISTA;
    actual = paso.alSaltar ?? 0;
    evaluar();
  }

  /**
   * Fin de la vista general. El recorrido no se detiene: vuelve, en silencio,
   * al paso salteado del camino completo, y aparece en cuanto el usuario carga
   * ese archivo —en esta misma visita, sin tener que recargar—. Queda anotado
   * para retomarlo igual en la próxima.
   */
  function terminarVista(): void {
    guardarAvance(`explorado:${salteadoEn}`);
    lista = PASOS;
    actual = salteadoEn;
    salteadoEn = -1;
    silencioso = true;
    ocultar();
  }

  /** Decide qué mostrar según lo que hay en pantalla. Se llama ante cualquier cambio. */
  function evaluar(): void {
    if (terminoSinReporte()) { cerrar(); return; }
    while (actual < lista.length && lista[actual].saltarSi?.()) actual++;
    if (actual >= lista.length) {
      if (lista === VISTA) terminarVista(); else cerrar();
      return;
    }

    const paso = lista[actual];
    if (paso.accion === "hacer" && paso.hasta?.()) {
      silencioso = false;
      avanzar();
      return;
    }
    if (silencioso) { ocultar(); return; }

    const el = disponible(paso);
    if (!el) { ocultar(); return; }
    if (el !== objetivo || mostrado !== actual) mostrar(paso, el);
    const bloqueado = (paso.accion === "hacer" && paso.alSaltar === undefined)
      || (paso.accion === "escribir" && paso.valido ? !paso.valido() : false);
    if (siguiente.disabled !== bloqueado) siguiente.disabled = bloqueado;
    posicionar();
  }

  /**
   * Se retira, pero no enseguida. Entre un paso y el siguiente el elemento
   * suele faltar un instante —el diálogo que se cierra, el asistente que se
   * redibuja—, y retirar el velo en ese instante para volver a ponerlo al
   * siguiente hacía titilar la pantalla. Si el paso reaparece antes, nunca se
   * retiró; si no, se desvanece.
   */
  function ocultar(): void {
    objetivo = null;
    mostrado = -1;
    if (oculto || esperaParaOcultar !== undefined) return;
    esperaParaOcultar = setTimeout(() => {
      esperaParaOcultar = undefined;
      oculto = true;
      raiz.classList.add("recorrido--oculto");
    }, GRACIA_PARA_OCULTAR_MS);
  }

  function mostrar(paso: Paso, el: HTMLElement): void {
    objetivo = el;
    mostrado = actual;
    clearTimeout(esperaParaOcultar);
    esperaParaOcultar = undefined;
    if (oculto) {
      oculto = false;
      raiz.classList.remove("recorrido--oculto");
    }

    // Cuentan los pasos que aplican: uno que se saltea —«Reporte enviado» si no
    // se envió— no puede figurar como pendiente.
    const aplica = (p: Paso) => p.seccion === paso.seccion && !p.saltarSi?.();
    const deLaSeccion = lista.filter(aplica);
    const enSeccion = lista.slice(0, actual + 1).filter(aplica).length;
    // Arriba, dónde está en el ciclo; abajo, junto a las flechas, cuánto
    // falta de esta etapa: es lo que las flechas recorren.
    etiqueta.textContent = `${paso.seccion} · Etapa ${SECCIONES.indexOf(paso.seccion) + 1} de ${SECCIONES.length}`;
    progreso.textContent = `${enSeccion} de ${deLaSeccion.length}`;
    titulo.textContent = paso.titulo;
    texto.textContent = paso.texto;
    // El primer paso es el índice 0: se compara con null, no por verdad.
    atras.disabled = anteriorDisponible() === null && !(lista === VISTA && salteadoEn >= 0);
    const ultimo = actual === lista.length - 1;
    siguiente.innerHTML = icon(ultimo ? "check" : "arrow");
    siguiente.setAttribute("aria-label", ultimo ? "Terminar el recorrido" : "Paso siguiente");

    // El deslizamiento entre pasos dura lo que la transición. Fuera de ese
    // momento el velo no se anima: si lo hiciera, al scrollear quedaría
    // corriendo detrás del contenido.
    // Si el elemento no está entero a la vista, primero hay que scrollear, y
    // dos movimientos a la vez —el scroll y el deslizamiento— se desfasan: el
    // velo sigue entonces al contenido cuadro a cuadro, sin transición.
    const r = el.getBoundingClientRect();
    const v = parteVisible(el);
    const entero = v !== null && Math.abs(v.height - r.height) < 1 && Math.abs(v.width - r.width) < 1;
    clearTimeout(finDelCambio);
    if (entero) {
      raiz.classList.add("recorrido--cambiando");
      finDelCambio = setTimeout(() => raiz.classList.remove("recorrido--cambiando"), DURACION_CAMBIO_MS);
    } else {
      raiz.classList.remove("recorrido--cambiando");
      el.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" });
    }
    // Al campo que hay que completar va el cursor: el usuario escribe sin
    // tener que hacer clic primero. Con una pausa, porque el diálogo que se
    // acaba de abrir pone su foco inicial en el cuadro siguiente.
    if (paso.accion === "escribir") {
      const campo = el.querySelector<HTMLElement>("input, select, textarea");
      setTimeout(() => { if (mostrado === actual) campo?.focus({ preventScroll: true }); }, PAUSA_FOCO_MS);
    }
  }

  function anteriorDisponible(): number | null {
    for (let i = actual - 1; i >= 0; i--) {
      if (lista[i].saltarSi?.()) continue;
      return disponible(lista[i]) ? i : null;
    }
    return null;
  }

  /**
   * Lo que queda sin velo: el elemento del paso y lo que haya que ver junto
   * con él, recortado a lo que de verdad se ve. La vista del archivo es más
   * alta que el asistente y scrollea adentro: sin el recorte, el hueco se
   * saldría del diálogo y dejaría a la vista el encabezado y el pie.
   */
  function descubiertos(): DOMRect[] {
    const paso = lista[actual];
    const extra = (paso?.tambien ?? []).map(s => visible(s)).filter((e): e is HTMLElement => Boolean(e));
    return [objetivo!, ...extra].map(parteVisible).filter((r): r is DOMRect => r !== null);
  }

  function posicionar(): void {
    if (!objetivo?.isConnected) return;
    const rects = descubiertos();

    // Un hueco por elemento. Siguen al contenido en el mismo cuadro en que se
    // mueve; solo al cambiar de paso se deslizan de un lugar al otro.
    while (huecos.children.length < rects.length) {
      const hueco = document.createElementNS("http://www.w3.org/2000/svg", "rect");
      hueco.setAttribute("class", "recorrido__hueco");
      huecos.appendChild(hueco);
    }
    Array.from(huecos.children).forEach((hueco, i) => {
      const r = rects[i];
      const estilo = (hueco as SVGRectElement).style;
      if (!r) { estilo.display = "none"; return; }
      Object.assign(estilo, {
        display: "", x: `${r.left - MARGEN_FOCO}px`, y: `${r.top - MARGEN_FOCO}px`,
        width: `${r.width + MARGEN_FOCO * 2}px`, height: `${r.height + MARGEN_FOCO * 2}px`,
      });
    });

    if (innerWidth < ANCHO_ANGOSTO) { tarjeta.removeAttribute("style"); return; }
    // Mientras el elemento está fuera de la vista —el scroll lo está trayendo—,
    // la tarjeta espera donde estaba.
    if (!rects.length) return;
    const { width: ancho, height: alto } = tarjeta.getBoundingClientRect();
    const { left, top } = ubicarTarjeta(rects, ancho, alto);
    Object.assign(tarjeta.style, { top: `${top}px`, left: `${left}px` });
  }

  function cerrar(): void {
    detenerActivo = null;
    controlador.abort();
    observador.disconnect();
    clearInterval(reloj);
    raiz.remove();
    marcarCerrado();
  }

  // Un clic en el elemento de un paso «clic» cumple el paso. Se escucha en
  // captura para enterarse antes de que la aplicación cambie de pantalla, y se
  // avanza después, cuando ya cambió.
  document.addEventListener("click", e => {
    const paso = lista[actual];
    if (oculto || paso?.accion !== "clic" || !objetivo?.contains(e.target as Node)) return;
    if (botonDe(objetivo)?.disabled) return;
    setTimeout(avanzar, 0);
  }, { ...senal, capture: true });

  // Enter en un campo válido avanza, como →.
  document.addEventListener("keydown", e => {
    const paso = lista[actual];
    if (e.key !== "Enter" || oculto || paso?.accion !== "escribir" || !objetivo?.contains(e.target as Node)) return;
    if (!siguiente.disabled) { e.preventDefault(); avanzar(); }
  }, { ...senal, capture: true });

  // Las flechas son del recorrido, salvo mientras se escribe en un campo:
  // ahí mueven el cursor.
  window.addEventListener("keydown", e => {
    if (oculto || esEditable(document.activeElement)) return;
    if (e.key === "ArrowRight" && !siguiente.disabled) siguiente.click();
    else if (e.key === "ArrowLeft" && !atras.disabled) atras.click();
    else return;
    e.preventDefault();
    e.stopImmediatePropagation();
  }, { ...senal, capture: true });

  siguiente.addEventListener("click", () => {
    const paso = lista[actual];
    if (paso.accion === "clic") botonDe(objetivo)?.click();
    else if (paso.accion === "hacer" && paso.alSaltar !== undefined) saltear(paso);
    else avanzar();
  }, senal);
  atras.addEventListener("click", () => {
    const anterior = anteriorDisponible();
    if (anterior !== null) { actual = anterior; evaluar(); return; }
    // Desde el principio de la vista general, ← vuelve al paso salteado.
    if (lista === VISTA && salteadoEn >= 0) {
      lista = PASOS;
      actual = salteadoEn;
      evaluar();
    }
  }, senal);
  omitir.addEventListener("click", cerrar, senal);

  window.addEventListener("resize", posicionar, senal);
  // Si algo scrollea mientras el velo se desliza —el asistente baja solo al
  // resultado de verificar, por ejemplo—, el deslizamiento se corta y el velo
  // pasa a seguir el scroll: con los dos a la vez, quedaría atrasado.
  window.addEventListener("scroll", () => {
    if (raiz.classList.contains("recorrido--cambiando")) {
      clearTimeout(finDelCambio);
      raiz.classList.remove("recorrido--cambiando");
    }
    posicionar();
  }, { ...senal, capture: true, passive: true });
  document.addEventListener("input", evaluar, senal);
  // Los cambios del propio recorrido no cuentan: reaccionar a ellos lo
  // pondría a evaluarse a sí mismo sin fin.
  const observador = new MutationObserver(registros => {
    if (registros.some(r => !raiz.contains(r.target))) evaluar();
  });
  observador.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["hidden", "disabled", "class"] });
  // Los estados que no mueven el DOM —un botón que se habilita tras una
  // respuesta— igual se notan.
  const reloj = setInterval(evaluar, INTERVALO_MS);

  detenerActivo = () => {
    detenerActivo = null;
    controlador.abort();
    observador.disconnect();
    clearInterval(reloj);
    raiz.remove();
  };
  evaluar();
}
